import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Chip, Switch, FormControlLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { handleAgentSelection, isAgenticModeEnabled } from '../../utils/agenticHelper';

/**
 * Multi Row Selector Template
 * Based on Figma design: https://www.figma.com/design/Nhga9ODmaPGwSQJjO8lQZi/Calibrate-Mate?node-id=394-10113&m=dev
 * Example: Location Discovery
 * 
 * Design specs from Figma:
 * - Chat bubble with 10px border radius (10px 10px 10px 0px)
 * - Row wrapper with 12px border radius, stroke rgba(192, 197, 205, 0.5)
 * - Selected chips: #E1EFFF background, #0F42BC text
 * - Unselected chips: #FFFFFF background, #465366 text, #EAECF0 border
 * - Sub-headers with 500 font weight, 14px size
 */
const MultiRowSelectorTemplate = ({
  title = "Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨",
  sections = [],
  selectedItems = [],
  onConfirmed,
  confirmButtonText = "Confirm Location",
  readOnly = false,
  allowEmptySelection = false,
  showSkipButton = false,
  onSkipDiscovery,
  skipButtonText = "Skip Discovery",
  // Agentic flow props
  enableAgenticToggle = true,
  agenticContext = '',
  topic = 'selection',
  sessionId = 'default-session'
}) => {
  console.log('🎯 MULTIROW TEMPLATE PROPS:', {
    title,
    sections,
    sectionsLength: sections.length,
    firstSectionItems: sections.length > 0 ? sections[0].items : 'no sections',
    selectedItems,
    readOnly
  });
  
  // Agentic flow state
  const [agenticModeEnabled, setAgenticModeEnabled] = useState(isAgenticModeEnabled());
  const [agentProcessing, setAgentProcessing] = useState(false);
  const [agentReasoning, setAgentReasoning] = useState('');
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const [selectionAttemptId, setSelectionAttemptId] = useState(null); // Track current selection attempt
  const [selectionCompleted, setSelectionCompleted] = useState(false); // Track if agent has successfully made selections

  // No auto-selection logic needed - use explicit selectedItems
  // Start with the provided selectedItems (could be empty array for user selection)
  const initialSelectedItems = selectedItems;
  const [localSelectedItems, setLocalSelectedItems] = useState(initialSelectedItems);
  console.log('localSelectedItems', localSelectedItems)
  // Update local state only when selectedItems prop explicitly changes (not when empty)
  useEffect(() => {
    // Only update if selectedItems prop has actual values - don't reset user selections
    if (selectedItems.length > 0) {
      setLocalSelectedItems(selectedItems);
    }
    // Don't reset to allAvailableItems if user has made selections and then selectedItems becomes empty
  }, [selectedItems]);

  const handleItemToggle = (item) => {
    if (readOnly) return;
    
    // Reset selection completion flag when user makes manual changes
    setSelectionCompleted(false);
    setAgentReasoning(''); // Clear agent reasoning when user manually changes
    
    setLocalSelectedItems(prev => {
      if (prev.includes(item)) {
        return prev.filter(i => i !== item);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleConfirm = () => {
    if (onConfirmed) {
      onConfirmed(localSelectedItems);
    }
  };

  // Agentic flow handlers
  const handleAgenticToggle = (event) => {
    const enabled = event.target.checked;
    
    // Prevent multiple toggles while processing
    if (agentProcessing) {
      console.log('🚫 TOGGLE BLOCKED: Agent is currently processing');
      return;
    }
    
    setAgenticModeEnabled(enabled);
    
    // Import the agentic helper functions
    import('../../utils/agenticHelper').then(({ enableAgenticMode, disableAgenticMode }) => {
      if (enabled) {
        enableAgenticMode(sessionId);
        console.log('🤖 AGENTIC MODE: Enabled globally for session:', sessionId);
        console.log('🤖 AGENTIC MODE: Template toggled, about to trigger selection');
        // Auto-trigger agent selection when toggled on (with debounce)
        if (!agentProcessing && !selectionAttemptId && !selectionCompleted) {
          setTimeout(() => {
            handleAgentAutoSelect();
          }, 100); // Small debounce to prevent rapid fire
        }
      } else {
        disableAgenticMode();
        console.log('👤 MANUAL MODE: Disabled agentic flow globally');
      }
    });
  };

  const handleAgentAutoSelect = useCallback(async () => {
    if (readOnly || agentProcessing) return;
    
    // Generate unique attempt ID to prevent duplicate processing
    const attemptId = `${topic}_${sessionId}_${Date.now()}`;
    
    // Check if this exact attempt is already in progress
    if (selectionAttemptId === attemptId) {
      console.log('🚫 AGENT SELECTION: Duplicate attempt prevented:', attemptId);
      return;
    }
    
    setSelectionAttemptId(attemptId);
    setAgentProcessing(true);
    setAgentReasoning('');
    
    console.log('🤖 AGENT SELECTION: Starting attempt:', attemptId);
    
    try {
      // Get all available options from sections
      const allOptions = sections.flatMap(section => 
        section.items?.map(item => ({
          label: typeof item === 'string' ? item : (item.label || item.name || item.value),
          value: typeof item === 'string' ? item : (item.value || item.label || item.name)
        })) || []
      );

      const strategyContext = {
        topic,
        options: allOptions,
        context: agenticContext || `${title}\n\nPlease select the most relevant options for this ${topic} discovery.`,
        selectionType: 'multi-select',
        systemMessage: `You are a recruiter helping to calibrate candidate search criteria for ${topic} selection. Be strategic and select 2-4 most relevant options that match the hiring context.`
      };

      const agentSelection = await handleAgentSelection(strategyContext);
      
      if (agentSelection && agentSelection.selected_options && agentSelection.selected_options.length > 0) {
        console.log('🤖 AGENT SELECTION: Raw agent selections:', agentSelection.selected_options);
        console.log('🤖 AGENT SELECTION: Available options:', allOptions);
        
        // Map agent selections back to actual item values
        const selectedValues = [];
        
        for (const agentChoice of agentSelection.selected_options) {
          // Extract queryId from formatted response like "4. Public (value: 5eb3ba57d4d733000746ecfc)"
          let extractedQueryId = null;
          const valueMatch = agentChoice.match(/value:\s*([a-fA-F0-9]+)\)/);
          if (valueMatch) {
            extractedQueryId = valueMatch[1];
            console.log('🔍 AGENT SELECTION: Extracted queryId:', extractedQueryId, 'from:', agentChoice);
          }
          
          // Try to match by extracted queryId first, then by label/value
          const matchedItem = allOptions.find(item => 
            (extractedQueryId && item.value === extractedQueryId) ||
            item.label === agentChoice || 
            item.value === agentChoice ||
            item.label?.toLowerCase() === agentChoice.toLowerCase() ||
            item.value?.toLowerCase() === agentChoice.toLowerCase()
          );
          
          if (matchedItem) {
            selectedValues.push(matchedItem.value);
            console.log('✅ AGENT SELECTION: Matched', agentChoice, 'to value:', matchedItem.value);
          } else {
            console.warn('🚨 AGENT SELECTION: Could not match agent choice:', agentChoice);
            // If we extracted a queryId, use that; otherwise use the raw choice
            selectedValues.push(extractedQueryId || agentChoice);
          }
        }
        
        console.log('🎯 AGENT SELECTION: Mapped to values:', selectedValues);
        
        setLocalSelectedItems(selectedValues);
        setAgentReasoning(agentSelection.reasoning || 'AI Assistant made these selections');
        setSelectionCompleted(true); // Mark selection as completed to prevent re-triggering
        
        console.log('✅ AGENT SELECTION: Successfully applied', selectedValues.length, 'selections');
      } else {
        console.error('🚨 AGENT SELECTION: No valid selections received');
        setAgentReasoning('Unable to make automatic selection. Please select manually.');
      }
      
    } catch (error) {
      console.error('🚨 AGENT AUTO-SELECT: Error:', error);
      setAgentReasoning('Error occurred during automatic selection. Please select manually.');
    } finally {
      setAgentProcessing(false);
      setSelectionAttemptId(null); // Clear attempt ID when done
      console.log('🤖 AGENT SELECTION: Completed attempt:', attemptId);
    }
  }, [readOnly, agentProcessing, sections, topic, agenticContext, title, selectionAttemptId]);

  // Auto-trigger agent selection if agentic mode is already enabled
  useEffect(() => {
    if (isAgenticModeEnabled() && !agentProcessing && !selectionAttemptId && sections.length > 0 && !hasAutoTriggered && !selectionCompleted) {
      console.log('🤖 AUTO-TRIGGER: Agentic mode already enabled, auto-triggering selection');
      setHasAutoTriggered(true);
      setAgenticModeEnabled(true);
      // Increased delay and additional checks to prevent conflicts
      setTimeout(() => {
        if (!agentProcessing && !selectionAttemptId) {
          handleAgentAutoSelect();
        }
      }, 750); // Longer delay to ensure UI is ready and no conflicts
    }
  }, [sections.length, hasAutoTriggered, selectionCompleted]); // Include selectionCompleted to prevent re-triggers

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        padding: '0px',
        backgroundColor: 'transparent',
        marginLeft: 0, // Align with chat bubble text
      }}
    >
      {/* Chat Bubble Container */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px 10px 10px 0px',
          padding: '0px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px', // Figma spec
          marginLeft: 0, // Start at same position as AI text
        }}
      >

        {/* Row Wrapper */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)', // Exact Figma color
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          {sections.map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignSelf: 'stretch',
              }}
            >
              {/* Chip Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  alignSelf: 'stretch',
                }}
              >
                {section.items.map((item, itemIndex) => {
                  const itemValue = item.value || item;
                  
                  // ✅ Fix: Ensure itemLabel is always a string
                  let itemLabel;
                  if (typeof item === 'object' && item !== null) {
                    // If item is an object, try to get label, name, or convert to string
                    itemLabel = item.label || item.name || item.value || String(item);
                  } else {
                    // If item is already a string or primitive, use it directly
                    itemLabel = String(item);
                  }
                  
                  // ✅ Fixed: Use consistent selection logic - always use itemValue for consistency
                  const isSelected = localSelectedItems.includes(itemValue);
                  
                  console.log('🔍 MULTIROW SELECTION DEBUG:', {
                    sectionIndex,
                    itemIndex,
                    item,
                    itemValue,
                    itemLabel,
                    localSelectedItems,
                    localSelectedItemsTypes: localSelectedItems.map(s => typeof s),
                    itemValueType: typeof itemValue,
                    isSelected: localSelectedItems.includes(itemValue),
                    exactMatches: localSelectedItems.filter(s => s === itemValue),
                    partialMatches: localSelectedItems.filter(s => String(s) === String(itemValue))
                  });
                  
                  return (
                    <Chip
                      key={itemIndex}
                      icon={
                        isSelected ? (
                          <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />
                        )
                      }
                      label={itemLabel}
                      onClick={() => handleItemToggle(itemValue)}
                      sx={{
                        backgroundColor: isSelected ? '#E1EFFF' : '#FFFFFF', // Figma colors
                        color: isSelected ? '#0F42BC' : '#465366', // Figma colors
                        // border: isSelected ? 'none' : '1px solid #EAECF0', // Figma border
                        borderRadius: '16px',
                        padding: '4px 12px 4px 10px',
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.4285714285714286em',
                        cursor: readOnly ? 'default' : 'pointer',
                        '& .MuiChip-icon': {
                          color: isSelected ? '#0F42BC' : '#465366',
                          fontSize: '14px',
                          fontFamily: 'Material Icons Round',
                        },
                        '&:hover': readOnly ? {} : {
                          backgroundColor: isSelected ? '#D1E7FF' : '#F9FAFB',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Agentic Toggle Row */}
        {enableAgenticToggle && !readOnly && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingX: '20px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={agenticModeEnabled}
                  onChange={handleAgenticToggle}
                  disabled={agentProcessing}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4599FA',
                      '&:hover': {
                        backgroundColor: 'rgba(69, 153, 250, 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4599FA',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: agenticModeEnabled ? '#4599FA' : '#9CA3AF' }} />
                  <Typography variant="body2" sx={{ fontSize: 14, color: agenticModeEnabled ? '#4599FA' : '#9CA3AF' }}>
                    {agentProcessing ? 'AI Assistant is selecting...' : 'Let AI Assistant select for me'}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
            {agentReasoning && (
              <Typography variant="caption" sx={{ fontSize: 12, color: '#667085', marginLeft: 4 }}>
                🤖 {agentReasoning}
              </Typography>
            )}
          </Box>
        )}

        {/* Buttons Row - Confirm and Skip side by side */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
          <Button
            variant="contained"
            onClick={readOnly ? undefined : handleConfirm}
            disabled={readOnly || (!allowEmptySelection && localSelectedItems.length === 0)}
            sx={{
              backgroundColor: readOnly ? '#E5E7EB' : '#4599FA', // Gray background for read-only
              color: readOnly ? '#9CA3AF' : '#FFFFFF', // Gray text for read-only
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              height: '36px',
              cursor: readOnly ? 'default' : 'pointer',
              '&:hover': readOnly ? {} : {
                backgroundColor: '#3b82d6',
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
              }
            }}
          >
            {readOnly ? 'Confirmed' : (localSelectedItems.length === 0 && allowEmptySelection ? 'Continue' : confirmButtonText)}
          </Button>
          
          {/* Skip Button - only show for editable items */}
          {showSkipButton && !readOnly && (
            <Button
              variant="outlined"
              onClick={onSkipDiscovery}
              sx={{
                color: '#667085',
                borderColor: '#D0D5DD',
                backgroundColor: '#F9FAFB',
                fontWeight: 400,
                fontSize: '14px',
                padding: '8px 16px',
                height: '36px',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#F2F4F7',
                  borderColor: '#B8BCC8',
                }
              }}
            >
              {skipButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MultiRowSelectorTemplate;
